import { LoginState, LoginType } from "../../models/enum/login";
import { Button } from "../common/button";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useLogin } from "../../hooks/useLogin";
import { useNavigate, useSearchParams } from "react-router-dom";
import { validateEmail } from "../../utils/validator";
import { toast } from "react-toastify";
import { NetworkError } from "../../models/networkError";
import { StoreKey } from "../../models/cacheKey";
import { GoogleLoginButton } from "./googleLoginButton";
import { t } from "i18next";

export function LoginForm({ type }: { type: LoginType }) {
  const navigate = useNavigate();
  const [loginState, setLoginState] = useState<LoginState>(LoginState.READY);
  const [email, setEmail] = useState<string>("");
  const [eventId, setEventId] = useState<number>();
  const [otp, setOtp] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    oneDayLogin,
    weAuthLogin,
    weAuthRequestOtp,
    isLoginPending,
    verifyOtp,
  } = useLogin();

  useEffect(() => {
    setLoginState(LoginState.READY);
    setEventId(undefined);
  }, [type]);

  useEffect(() => {
    if (loginState !== LoginState.WAITING_OTP) {
      setOtp("");
      setEventId(undefined);
    }
  }, [loginState]);

  useEffect(() => {
    const phase = searchParams.get(StoreKey.loginPhase);
    if (phase === "otp") {
      searchParams.delete(StoreKey.loginPhase);
      setSearchParams(searchParams);
      weAuthRequestOtp()
        .then(() => {
          setLoginState(LoginState.WAITING_OTP);
        })
        .catch((e) => {
          handleError(e);
        });
    } else if (phase === "no_grant") {
      searchParams.delete(StoreKey.loginPhase);
      setSearchParams(searchParams);
      setTimeout(() => {
        toast.error(t("logingate_alert_noauth"));
      }, 0);
    }
  }, [searchParams, setSearchParams, weAuthRequestOtp]);

  const getLoginButtonLabel = () => {
    switch (loginState) {
      case LoginState.READY:
        return t("logingate_authbutton");
      case LoginState.WAITING_OTP:
        return t("logingate_loginbutton");
    }
  };

  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const onChangeEventId = (e: ChangeEvent<HTMLInputElement>) => {
    setEventId(Number(e.target.value));
  };

  const onChangeOtp = (e: ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value);
  };

  const onClickLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (loginState === LoginState.READY) {
      if (type === LoginType.WEAUTH) {
        await weAuthLogin();
      } else if (type === LoginType.ONE_DAY_STAFF) {
        if (!validateEmail(email)) {
          toast.error(t("logingate_alert_notemail"));
          return;
        }
        if (eventId) {
          try {
            await oneDayLogin({ email, eventId });
            setLoginState(LoginState.WAITING_OTP);
          } catch (e) {
            handleError(e);
          }
        } else {
          toast.error(t("logingate_alert_notid"));
        }
      } else {
        toast.error("Not defined");
      }
    } else if (loginState === LoginState.WAITING_OTP) {
      try {
        const isSecurityPledgeRequired = await verifyOtp(type, otp);
        // 보안 서약서 필요 시 보안 서약서 페이지로 이동
        if (isSecurityPledgeRequired) {
          navigate("/security-pledge", { replace: true });
          return;
        }
        setLoginState(LoginState.LOGGED_IN);
        setOtp("");
        navigate("/events/checkins");
      } catch (e) {
        handleError(e);
      }
    }
  };

  const handleError = (e: any) => {
    if (!(e instanceof NetworkError)) {
      toast.error(`${e}`);
    } else {
      toast.error(
        t(e.body?.tag || "logingate_alert_notuser", {
          n: e.body?.args[0],
        }),
      );
    }
  };

  return (
    <form onSubmit={onClickLogin}>
      <div className={"grow flex flex-col gap-2"}>
        {loginState === LoginState.READY && (
          <>
            {type === LoginType.WEAUTH && <></>}
            {type === LoginType.ONE_DAY_STAFF && (
              <>
                <label className="input input-bordered flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    className="w-4 h-4 opacity-70"
                  >
                    <path d="M2.5 3A1.5 1.5 0 0 0 1 4.5v.793c.026.009.051.02.076.032L7.674 8.51c.206.1.446.1.652 0l6.598-3.185A.755.755 0 0 1 15 5.293V4.5A1.5 1.5 0 0 0 13.5 3h-11Z" />
                    <path d="M15 6.954 8.978 9.86a2.25 2.25 0 0 1-1.956 0L1 6.954V11.5A1.5 1.5 0 0 0 2.5 13h11a1.5 1.5 0 0 0 1.5-1.5V6.954Z" />
                  </svg>
                  <input
                    inputMode={"email"}
                    name="email"
                    type="email"
                    className="grow"
                    placeholder={t("logingate_email")}
                    value={email}
                    onChange={onChangeEmail}
                  />
                </label>
                <label className="input input-bordered flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    className="w-4 h-4 opacity-70"
                  >
                    <path
                      fillRule="evenodd"
                      d="M1 4.5A1.5 1.5 0 0 1 2.5 3h11A1.5 1.5 0 0 1 15 4.5v1c0 .276-.227.494-.495.562a2 2 0 0 0 0 3.876c.268.068.495.286.495.562v1a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 11.5v-1c0-.276.227-.494.495-.562a2 2 0 0 0 0-3.876C1.227 5.994 1 5.776 1 5.5v-1Zm9 1.25a.75.75 0 0 1 1.5 0v1a.75.75 0 0 1-1.5 0v-1Zm.75 2.75a.75.75 0 0 0-.75.75v1a.75.75 0 0 0 1.5 0v-1a.75.75 0 0 0-.75-.75Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <input
                    inputMode={"numeric"}
                    type="number"
                    className="grow"
                    placeholder={t("eventdetail_eventid")}
                    value={!eventId ? "" : eventId}
                    onChange={onChangeEventId}
                  />
                </label>
              </>
            )}
          </>
        )}
        {loginState === LoginState.WAITING_OTP && (
          <>
            <label className="input input-bordered flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="w-4 h-4"
              >
                <path
                  fillRule="evenodd"
                  d="M8.5 1.709a.75.75 0 0 0-1 0 8.963 8.963 0 0 1-4.84 2.217.75.75 0 0 0-.654.72 10.499 10.499 0 0 0 5.647 9.672.75.75 0 0 0 .694-.001 10.499 10.499 0 0 0 5.647-9.672.75.75 0 0 0-.654-.719A8.963 8.963 0 0 1 8.5 1.71Zm2.34 5.504a.75.75 0 0 0-1.18-.926L7.394 9.17l-1.156-.99a.75.75 0 1 0-.976 1.138l1.75 1.5a.75.75 0 0 0 1.078-.106l2.75-3.5Z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                inputMode={"numeric"}
                type="number"
                className="grow"
                placeholder={t("logingate_otpcode")}
                value={otp}
                onChange={onChangeOtp}
              />
            </label>
          </>
        )}
        {loginState === LoginState.READY && type === LoginType.WEAUTH ? (
          <GoogleLoginButton loading={isLoginPending} />
        ) : (
          <Button type={"submit"} loading={isLoginPending}>
            {getLoginButtonLabel()}
          </Button>
        )}
        {loginState === LoginState.WAITING_OTP && (
          <Button
            type={"button"}
            className={"mt-3 btn btn-ghost btn-xs"}
            onClick={async () => {
              try {
                if (type === LoginType.ONE_DAY_STAFF) {
                  if (eventId) {
                    await oneDayLogin({ email, eventId });
                  }
                } else if (type === LoginType.WEAUTH) {
                  await weAuthRequestOtp();
                }
                toast.info(t("logingate_alert_resend"));
              } catch (e) {
                handleError(e);
              }
            }}
            loading={isLoginPending}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              fill="currentColor"
              className="w-4 h-4"
            >
              <path
                fillRule="evenodd"
                d="M13.836 2.477a.75.75 0 0 1 .75.75v3.182a.75.75 0 0 1-.75.75h-3.182a.75.75 0 0 1 0-1.5h1.37l-.84-.841a4.5 4.5 0 0 0-7.08.932.75.75 0 0 1-1.3-.75 6 6 0 0 1 9.44-1.242l.842.84V3.227a.75.75 0 0 1 .75-.75Zm-.911 7.5A.75.75 0 0 1 13.199 11a6 6 0 0 1-9.44 1.241l-.84-.84v1.371a.75.75 0 0 1-1.5 0V9.591a.75.75 0 0 1 .75-.75H5.35a.75.75 0 0 1 0 1.5H3.98l.841.841a4.5 4.5 0 0 0 7.08-.932.75.75 0 0 1 1.025-.273Z"
                clipRule="evenodd"
              />
            </svg>
            {t("logingate_resendotp")}
          </Button>
        )}
      </div>
    </form>
  );
}
